import { api } from '../../lib/api';
import { firebaseAuth } from '../../lib/firebase';
import type {
  MetaAdDraft,
  MetaAdDraftInput,
  MetaAdDraftSummary,
  PublishError,
} from '../../types/metaAdDraft';
import type { MetaAdValidationIssue } from '../../lib/metaAdValidation';
import type {
  AdAccountSummary,
  AdTreeResult,
  CampaignSummary,
  CreateLeadFormInput,
  EligibleAccountsResult,
  LeadFormDetail,
  LeadFormSummary,
  PageSummary,
  PixelSummary,
  ReachEstimateResult,
  TargetingSearchResult,
} from '../../types/metaAdResources';
import type { TargetingConfig } from '../../types/metaAdDraft';
import type { CloneRequestBody, CloneResponse, DuplicateResult, LiveAd, LiveCampaignDetail, LiveCampaignsPage, LiveStatusFilter } from '../../types/metaLiveCampaigns';
import type {
  SavedAudience,
  SavedAudienceSource,
} from '../../types/savedAudience';

// Stage 7A C2 — client calls for the Meta Ad Builder compose flow.

export type DraftResponse = { draft: MetaAdDraft; composeWarnings: MetaAdValidationIssue[] };

export function getDraft(id: string): Promise<DraftResponse> {
  return api<DraftResponse>(`/api/meta-ads/drafts/${id}`);
}

export function createDraft(input: MetaAdDraftInput): Promise<DraftResponse> {
  return api<DraftResponse>('/api/meta-ads/drafts', { method: 'POST', body: JSON.stringify(input) });
}

// E1 — all draft summaries (incl. archived); the page filters/sorts/searches client-side.
export function listDrafts(): Promise<{ drafts: MetaAdDraftSummary[] }> {
  return api<{ drafts: MetaAdDraftSummary[] }>('/api/meta-ads/drafts');
}

// Soft-delete (= archive). Server sets deletedAt/deletedBy; the row moves to the
// Archived filter.
export function archiveDraft(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/api/meta-ads/drafts/${id}`, { method: 'DELETE' });
}

// Duplicate (D2.16) — clone a draft OR published campaign into a fresh editable
// draft. Returns the new draft (the caller redirects to its compose page).
export function duplicateDraft(id: string, newTitle?: string): Promise<DraftResponse> {
  return api<DraftResponse>(`/api/meta-ads/drafts/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ newTitle }),
  });
}

// PATCH carries the client's version for the CAS check; a 409 throws ApiError.
export function patchDraft(
  id: string,
  input: MetaAdDraftInput,
  version: number,
): Promise<DraftResponse> {
  return api<DraftResponse>(`/api/meta-ads/drafts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...input, version }),
  });
}

export function getEligibleAccounts(): Promise<EligibleAccountsResult> {
  return api<EligibleAccountsResult>('/api/meta-ads/eligible-accounts');
}

// ---------- publish (D3) ----------

export type PublishWarning = { code: string; message: string };

export type PublishSuccess = {
  publishedMetaAdId: string | null;
  publishedMetaAdSetId: string | null;
  publishedMetaCampaignId: string | null;
  publishedAt: string | null;
  draft: MetaAdDraft;
  warnings: PublishWarning[];
};

// The known non-2xx publish outcomes (D2.2). `failed` carries the structured
// publishError (category drives the error modal); `conflict` carries the server's
// current draft (to resync the version); `invalid` carries the validation issues.
export type PublishFailureKind = 'conflict' | 'locked' | 'in_progress' | 'invalid' | 'failed';

export class PublishFailedError extends Error {
  constructor(
    public readonly kind: PublishFailureKind,
    message: string,
    public readonly publishError: PublishError | null = null,
    public readonly issues: MetaAdValidationIssue[] = [],
    public readonly current: MetaAdDraft | null = null,
  ) {
    super(message);
    this.name = 'PublishFailedError';
  }
}

// Self-contained (own fetch + token) because the shared api() helper discards the
// structured error body, and the publish flow needs publishError/issues/current.
// Returns PublishSuccess on 200; throws a typed PublishFailedError otherwise.
export async function publishDraft(draftId: string, version: number): Promise<PublishSuccess> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new PublishFailedError('failed', 'Not signed in');
  const token = await user.getIdToken();

  const res = await fetch(`/api/meta-ads/drafts/${draftId}/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ version }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (res.ok) return body as unknown as PublishSuccess;

  const code = typeof body.error === 'string' ? body.error : undefined;
  if (res.status === 409 && code === 'version_conflict') {
    throw new PublishFailedError('conflict', 'version_conflict', null, [], (body.current as MetaAdDraft) ?? null);
  }
  if (res.status === 409 && code === 'locked') throw new PublishFailedError('locked', 'locked');
  if (res.status === 409 && code === 'publish_in_progress') {
    throw new PublishFailedError('in_progress', 'publish_in_progress');
  }
  if (res.status === 400 && code === 'validation') {
    throw new PublishFailedError('invalid', 'validation', null, (body.issues as MetaAdValidationIssue[]) ?? []);
  }
  // publish_failed (502, or a category-derived status) — structured publishError.
  const pe = (body.publishError as PublishError) ?? null;
  throw new PublishFailedError('failed', pe?.message ?? code ?? `HTTP ${res.status}`, pe);
}

// Used on-pick to resolve the linked ad account's currency + timezone.
export function getAdAccounts(accountId: string): Promise<{ adAccounts: AdAccountSummary[] }> {
  return api<{ adAccounts: AdAccountSummary[] }>(
    `/api/meta-ads/accounts/${accountId}/ad-accounts`,
  );
}

export function getPages(accountId: string): Promise<{ pages: PageSummary[] }> {
  return api<{ pages: PageSummary[] }>(`/api/meta-ads/accounts/${accountId}/pages`);
}

// Lead Gen forms for the instant-form destination picker (B2b.4). Server sorts
// newest-first.
export function getLeadForms(accountId: string): Promise<{ leadForms: LeadFormSummary[] }> {
  return api<{ leadForms: LeadFormSummary[] }>(`/api/meta-ads/accounts/${accountId}/lead-forms`);
}

// Lead Forms inline duplicate — fetch one form's FULL config to prefill a variant.
export function getLeadForm(accountId: string, formId: string): Promise<{ leadForm: LeadFormDetail }> {
  return api<{ leadForm: LeadFormDetail }>(
    `/api/meta-ads/accounts/${accountId}/lead-forms/${formId}`,
  );
}

// Lead Forms inline create — create a form on the connected Page; the returned
// formId is the leadGenFormId the builder sets on the ad.
export function createLeadForm(
  accountId: string,
  input: CreateLeadFormInput,
): Promise<{ formId: string; name: string }> {
  return api<{ formId: string; name: string }>(`/api/meta-ads/accounts/${accountId}/lead-forms`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Lead Forms management (Meta Setup hub) — archive a form (Meta has no hard
// delete; this sets status=ARCHIVED). The caller refreshes the list after.
export function archiveLeadForm(
  accountId: string,
  formId: string,
): Promise<{ formId: string; archived: boolean }> {
  return api<{ formId: string; archived: boolean }>(
    `/api/meta-ads/accounts/${accountId}/lead-forms/${formId}/archive`,
    { method: 'POST' },
  );
}

// Conversion pixels for the linked ad account (B2b.5).
export function getPixels(accountId: string): Promise<{ pixels: PixelSummary[] }> {
  return api<{ pixels: PixelSummary[] }>(`/api/meta-ads/accounts/${accountId}/pixels`);
}

// ---------- targeting search (B2b.7, live typeahead) ----------
type SearchResults = { results: TargetingSearchResult[] };

export function searchInterests(accountId: string, q: string): Promise<SearchResults> {
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/interests?q=${encodeURIComponent(q)}`,
  );
}
// Unified geo search (geoType=all) — one ranked, mixed-type call (replaces the per-type
// fan-out). Each result carries its own `type` + `path` breadcrumb.
export function searchAllGeo(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/all?q=${encodeURIComponent(q)}${c}`,
  );
}
export function searchCities(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/cities?q=${encodeURIComponent(q)}${c}`,
  );
}
export function searchRegions(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/regions?q=${encodeURIComponent(q)}${c}`,
  );
}
export function searchPlaces(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/places?q=${encodeURIComponent(q)}${c}`,
  );
}
export function searchNeighborhoods(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/neighborhoods?q=${encodeURIComponent(q)}${c}`,
  );
}
export function searchZips(accountId: string, q: string, country?: string | null): Promise<SearchResults> {
  const c = country ? `&country=${encodeURIComponent(country)}` : '';
  return api<SearchResults>(
    `/api/meta-ads/accounts/${accountId}/targeting/search/zips?q=${encodeURIComponent(q)}${c}`,
  );
}

// ---------- targeting extraction (C5) ----------

// List prior campaigns for the extraction picker. activeOnly=true (default) keeps
// active/paused within `daysSince`; false returns everything (the "Show all" toggle).
export function getCampaigns(
  accountId: string,
  opts: { activeOnly: boolean; daysSince: number },
): Promise<{ campaigns: CampaignSummary[] }> {
  const params = new URLSearchParams({
    activeOnly: String(opts.activeOnly),
    daysSince: String(opts.daysSince),
  });
  return api<{ campaigns: CampaignSummary[] }>(
    `/api/meta-ads/accounts/${accountId}/campaigns?${params.toString()}`,
  );
}

// Ad-tree for one campaign — ad sets + their ads (id/name/status). The tree omits
// targeting; C5 picks an ad id from here and calls getAdTargeting. The optional
// date window (from the picked campaign's ad activity, via adTreeDateWindow)
// narrows the server-side scan so the campaign resolves on heavy accounts (>600
// campaigns) instead of falling past the page cap.
export function getAdTree(
  accountId: string,
  campaignId: string,
  window?: { from?: string; to?: string },
): Promise<AdTreeResult> {
  const qs = new URLSearchParams();
  if (window?.from) qs.set('from', window.from);
  if (window?.to) qs.set('to', window.to);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<AdTreeResult>(`/api/meta-ads/accounts/${accountId}/campaigns/${campaignId}/ad-tree${suffix}`);
}

// Per-ad targeting (B2b.10) — the canonical extraction path. `found` is false when
// the ad carried no targeting signal.
export function getAdTargeting(
  accountId: string,
  adId: string,
): Promise<{ targeting: TargetingConfig; found: boolean }> {
  return api<{ targeting: TargetingConfig; found: boolean }>(
    `/api/meta-ads/accounts/${accountId}/ads/${adId}/targeting`,
  );
}

// ---------- reach estimate (B2b.8 / D1) ----------

// The server expects the RAW kyck TargetingConfig under `spec` and runs the
// kyckTargetingToZernioSpec transform server-side (router → estimateReachForAccount),
// which applies the shared metaCountries rule (city-only when cities/regions are
// present — sending the parent country never-readies the estimate, Miki 2026-06-15).
// Do NOT add countries to the spec here. optimizationGoal is derived from the goal.
export function estimateReach(
  accountId: string,
  spec: TargetingConfig,
  optimizationGoal?: string | null,
): Promise<ReachEstimateResult> {
  return api<ReachEstimateResult>(`/api/meta-ads/accounts/${accountId}/estimate-reach`, {
    method: 'POST',
    body: JSON.stringify({ spec, optimizationGoal: optimizationGoal ?? undefined }),
  });
}

// ---------- Saved Audiences (Feature B, Phase 1) ----------
// Account-scoped reusable audiences (targeting only). The builder surfaces Save +
// Apply per ad set; the server strips placements on create.

export function listSavedAudiences(accountId: string): Promise<{ savedAudiences: SavedAudience[] }> {
  return api<{ savedAudiences: SavedAudience[] }>(`/api/accounts/${accountId}/saved-audiences`);
}

export function createSavedAudience(
  accountId: string,
  body: { name: string; targeting: TargetingConfig; source: SavedAudienceSource | null },
): Promise<{ savedAudience: SavedAudience }> {
  return api<{ savedAudience: SavedAudience }>(`/api/accounts/${accountId}/saved-audiences`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function renameSavedAudience(
  accountId: string,
  audienceId: string,
  name: string,
): Promise<{ savedAudience: SavedAudience }> {
  return api<{ savedAudience: SavedAudience }>(
    `/api/accounts/${accountId}/saved-audiences/${audienceId}`,
    { method: 'PATCH', body: JSON.stringify({ name }) },
  );
}

// Toggle the "Account Default" flag (same PATCH endpoint, partial update). Account-
// default audiences are the ones the rollout Build-Campaign flow auto-seeds later.
export function setSavedAudienceDefault(
  accountId: string,
  audienceId: string,
  isAccountDefault: boolean,
): Promise<{ savedAudience: SavedAudience }> {
  return api<{ savedAudience: SavedAudience }>(
    `/api/accounts/${accountId}/saved-audiences/${audienceId}`,
    { method: 'PATCH', body: JSON.stringify({ isAccountDefault }) },
  );
}

export function deleteSavedAudience(accountId: string, audienceId: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/api/accounts/${accountId}/saved-audiences/${audienceId}`, {
    method: 'DELETE',
  });
}

// ---------- Build Campaign Phase 2 (Drive bytes → Blob) ----------

// Fetch a Drive file's raw bytes as a Blob (the service account reads them server-
// side; the browser can't — no Drive OAuth + CORS). The returned Blob feeds the
// EXISTING upload path (getAssetUploadUrl + putFileWithProgress) identically to a
// user file-pick. Own fetch + token (the shared api() helper assumes JSON).
export async function fetchDriveFileBlob(
  fileId: string,
): Promise<{ blob: Blob; contentType: string }> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`/api/drive/file/${encodeURIComponent(fileId)}/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = `Drive download failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body.error === 'string') msg = body.error;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const contentType = res.headers.get('Content-Type') || blob.type || 'application/octet-stream';
  return { blob, contentType };
}

// ---------- creative asset upload (C7.1) ----------

export type AssetUploadUrl = {
  uploadUrl: string;
  imagePath: string;
  imageUrl: string;
  requiredHeaders: Record<string, string>;
};

export function getAssetUploadUrl(body: {
  accountId: string;
  contentType: string;
  originalFilename: string;
}): Promise<AssetUploadUrl> {
  return api<AssetUploadUrl>('/api/meta-ads/assets/upload-url', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Direct PUT of the file bytes to the signed URL, with upload-progress events.
// Uses XMLHttpRequest because fetch() exposes no upload-progress stream. The
// signed URL already carries auth, so NO Authorization header here — only the
// exact headers the server told us to echo (Content-Type + object metadata).
export function putFileWithProgress(
  uploadUrl: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed — network error'));
    xhr.send(file);
  });
}

// ---------- "Add an ad to a live ad set" (read-only live campaign model) ----------

export function getLiveCampaigns(
  accountId: string,
  opts: { status: LiveStatusFilter; search: string; publishedByKyck: boolean; page: number },
): Promise<LiveCampaignsPage> {
  const params = new URLSearchParams({
    status: opts.status,
    search: opts.search,
    publishedByKyck: opts.publishedByKyck ? '1' : '0',
    page: String(opts.page),
  });
  return api<LiveCampaignsPage>(`/api/meta-ads/accounts/${accountId}/live-campaigns?${params.toString()}`);
}

export function getLiveCampaignDetail(accountId: string, campaignId: string): Promise<LiveCampaignDetail> {
  return api<LiveCampaignDetail>(
    `/api/meta-ads/accounts/${accountId}/live-campaigns/${encodeURIComponent(campaignId)}`,
  );
}

export function getLiveAd(accountId: string, campaignId: string, adId: string): Promise<LiveAd> {
  return api<LiveAd>(
    `/api/meta-ads/accounts/${accountId}/live-campaigns/${encodeURIComponent(campaignId)}/ads/${encodeURIComponent(adId)}`,
  );
}

// ---------- Clone to a new campaign (Part B) ----------

export function cloneCampaign(accountId: string, campaignId: string, body: CloneRequestBody): Promise<CloneResponse> {
  return api<CloneResponse>(
    `/api/meta-ads/accounts/${accountId}/live-campaigns/${encodeURIComponent(campaignId)}/clone`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// ---------- Duplicate exactly, paused (Part C) ----------

export function duplicateCampaignExactly(
  accountId: string,
  campaignId: string,
  body: { startDate?: string | null; endDate?: string | null },
): Promise<DuplicateResult> {
  return api<DuplicateResult>(
    `/api/meta-ads/accounts/${accountId}/live-campaigns/${encodeURIComponent(campaignId)}/duplicate`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
